import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from "react";

import { type SlowRpcAckRequest, useSlowRpcAckRequests } from "../rpc/requestLatencyState";
import {
  getWsConnectionStatus,
  getWsConnectionUiState,
  setBrowserOnlineStatus,
  type WsConnectionStatus,
  type WsConnectionUiState,
  useWsConnectionStatus,
} from "../rpc/wsConnectionState";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { getPrimaryEnvironmentConnection } from "../environments/runtime";
import { APP_SERVER_NAME } from "../branding";
import { t } from "~/i18n";

const FORCED_WS_RECONNECT_DEBOUNCE_MS = 5_000;
export const WS_RECONNECT_NOTIFICATION_GRACE_MS = 8_000;
type WsAutoReconnectTrigger = "focus" | "online";

const connectionTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
  second: "2-digit",
});

function formatConnectionMoment(isoDate: string | null): string | null {
  if (!isoDate) {
    return null;
  }

  return connectionTimeFormatter.format(new Date(isoDate));
}

function formatRetryCountdown(nextRetryAt: string, nowMs: number): string {
  const remainingMs = Math.max(0, new Date(nextRetryAt).getTime() - nowMs);
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return t("common/s_tqgrlz", { seconds });
}

export function formatReconnectAttemptLabel(status: WsConnectionStatus): string {
  return t("common/attempt_1um6i9", {
    reconnectAttempt: Math.max(1, status.reconnectAttemptCount),
  });
}

function getConnectionDisplayName(status: WsConnectionStatus): string {
  return status.connectionLabel?.trim() || APP_SERVER_NAME;
}

function buildReconnectTitle(status: WsConnectionStatus): string {
  return t("common/disconnectedFrom_63sidl", { "0": getConnectionDisplayName(status) });
}

function buildRecoveredTitle(status: WsConnectionStatus): string {
  return t("common/reconnectedTo_krwucr", { "0": getConnectionDisplayName(status) });
}

function describeRecoveredToast(
  previousDisconnectedAt: string | null,
  connectedAt: string | null,
): string {
  const reconnectedAtLabel = formatConnectionMoment(connectedAt);
  const disconnectedAtLabel = formatConnectionMoment(previousDisconnectedAt);

  if (disconnectedAtLabel && reconnectedAtLabel) {
    return t("common/disconnectedAtAndReconnected_hlb3d5", {
      disconnectedAtLabel,
      reconnectedAtLabel,
    });
  }

  if (reconnectedAtLabel) {
    return t("common/connectionRestoredAt_05hxfg", { reconnectedAtLabel });
  }

  return t("common/connectionRestored_4jp7g0");
}

function describeSlowRpcAckToast(requests: ReadonlyArray<SlowRpcAckRequest>): string {
  const count = requests.length;
  const thresholdSeconds = Math.round((requests[0]?.thresholdMs ?? 0) / 1000);

  return t("common/requestWaitingLongerThan_s7pbch", { count, thresholdSeconds });
}

function SlowRpcAckRequestDetails({ requests }: { requests: ReadonlyArray<SlowRpcAckRequest> }) {
  return (
    <ul className="space-y-2.5 text-xs text-muted-foreground">
      {requests.map((req) => (
        <li
          className="min-w-0 border-border/50 border-b pb-2 last:border-b-0 last:pb-0"
          key={req.requestId}
        >
          <div className="wrap-break-word font-medium text-foreground">{req.tag}</div>
          <div className="mt-0.5 font-mono text-[10px] leading-snug opacity-90">
            {req.requestId}
          </div>
          <div className="mt-0.5 text-[10px] opacity-75">
            {t("common/started_vid84o", {
              "0": formatConnectionMoment(req.startedAt) ?? req.startedAt,
            })}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function shouldAutoReconnect(
  status: WsConnectionStatus,
  trigger: WsAutoReconnectTrigger,
): boolean {
  const uiState = getWsConnectionUiState(status);

  if (trigger === "online") {
    return uiState === "offline" || uiState === "reconnecting" || uiState === "error";
  }

  return status.online && status.hasConnected && uiState === "reconnecting";
}

export function shouldRestartStalledReconnect(
  status: WsConnectionStatus,
  expectedNextRetryAt: string,
): boolean {
  return (
    status.reconnectPhase === "waiting" &&
    status.nextRetryAt === expectedNextRetryAt &&
    status.online &&
    status.hasConnected
  );
}

export function shouldShowConnectionOutageToast({
  documentVisible,
  nowMs,
  status,
  uiState,
}: {
  readonly documentVisible: boolean;
  readonly nowMs: number;
  readonly status: WsConnectionStatus;
  readonly uiState: WsConnectionUiState;
}): boolean {
  if (!documentVisible || !status.hasConnected || status.disconnectedAt === null) {
    return false;
  }

  if (uiState !== "offline" && uiState !== "reconnecting") {
    return false;
  }

  const disconnectedAtMs = new Date(status.disconnectedAt).getTime();
  return (
    Number.isFinite(disconnectedAtMs) &&
    nowMs - disconnectedAtMs >= WS_RECONNECT_NOTIFICATION_GRACE_MS
  );
}

export function shouldShowConnectionRecoveredToast({
  outageToastVisible,
  previousDisconnectedAt,
  previousUiState,
  uiState,
}: {
  readonly outageToastVisible: boolean;
  readonly previousDisconnectedAt: string | null;
  readonly previousUiState: WsConnectionUiState;
  readonly uiState: WsConnectionUiState;
}): boolean {
  return (
    outageToastVisible &&
    uiState === "connected" &&
    previousDisconnectedAt !== null &&
    (previousUiState === "offline" || previousUiState === "reconnecting")
  );
}

function isDocumentVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function WebSocketConnectionCoordinator() {
  const status = useWsConnectionStatus();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [documentVisible, setDocumentVisible] = useState(() => isDocumentVisible());
  const lastForcedReconnectAtRef = useRef(0);
  const toastIdRef = useRef<ReturnType<typeof toastManager.add> | null>(null);
  const toastKindRef = useRef<"outage" | "recovered" | null>(null);
  const toastResetTimerRef = useRef<number | null>(null);
  const outageToastVisibleRef = useRef(false);
  const previousUiStateRef = useRef<WsConnectionUiState>(getWsConnectionUiState(status));
  const previousDisconnectedAtRef = useRef<string | null>(status.disconnectedAt);

  const runReconnect = useEffectEvent((showFailureToast: boolean) => {
    if (toastResetTimerRef.current !== null) {
      window.clearTimeout(toastResetTimerRef.current);
      toastResetTimerRef.current = null;
    }
    lastForcedReconnectAtRef.current = Date.now();
    void getPrimaryEnvironmentConnection()
      .reconnect()
      .catch((error) => {
        if (!showFailureToast) {
          console.warn("Automatic WebSocket reconnect failed", { error });
          return;
        }
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t("common/reconnectFailed_9q0yjv"),
            description:
              error instanceof Error ? error.message : t("common/unableToRestartThe_i5m7u9"),
            data: {
              dismissAfterVisibleMs: 8_000,
              hideCopyButton: true,
            },
          }),
        );
      });
  });
  const syncBrowserOnlineStatus = useEffectEvent(() => {
    setBrowserOnlineStatus(navigator.onLine !== false);
  });
  const triggerManualReconnect = useEffectEvent(() => {
    runReconnect(true);
  });
  const triggerAutoReconnect = useEffectEvent((trigger: WsAutoReconnectTrigger) => {
    const currentStatus =
      trigger === "online" ? setBrowserOnlineStatus(true) : getWsConnectionStatus();

    if (!shouldAutoReconnect(currentStatus, trigger)) {
      return;
    }
    if (Date.now() - lastForcedReconnectAtRef.current < FORCED_WS_RECONNECT_DEBOUNCE_MS) {
      return;
    }

    runReconnect(false);
  });

  useEffect(() => {
    const handleOnline = () => {
      triggerAutoReconnect("online");
    };
    const handleFocus = () => {
      triggerAutoReconnect("focus");
    };
    const handleVisibilityChange = () => {
      setDocumentVisible(isDocumentVisible());
    };

    syncBrowserOnlineStatus();
    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", syncBrowserOnlineStatus);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", syncBrowserOnlineStatus);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  useEffect(() => {
    if (status.reconnectPhase !== "waiting" || status.nextRetryAt === null) {
      return;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [status.nextRetryAt, status.reconnectPhase]);

  useEffect(() => {
    if (
      status.reconnectPhase !== "waiting" ||
      status.nextRetryAt === null ||
      !status.online ||
      !status.hasConnected
    ) {
      return;
    }

    const nextRetryAt = status.nextRetryAt;
    const timeoutMs = Math.max(0, new Date(nextRetryAt).getTime() - Date.now()) + 1_500;
    const timeoutId = window.setTimeout(() => {
      const currentStatus = getWsConnectionStatus();
      if (!shouldRestartStalledReconnect(currentStatus, nextRetryAt)) {
        return;
      }

      runReconnect(false);
    }, timeoutMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    status.hasConnected,
    status.nextRetryAt,
    status.online,
    status.reconnectAttemptCount,
    status.reconnectPhase,
  ]);

  useEffect(() => {
    const uiState = getWsConnectionUiState(status);
    if (
      !documentVisible ||
      !status.hasConnected ||
      status.disconnectedAt === null ||
      (uiState !== "offline" && uiState !== "reconnecting")
    ) {
      return;
    }

    const disconnectedAtMs = new Date(status.disconnectedAt).getTime();
    if (!Number.isFinite(disconnectedAtMs)) {
      return;
    }

    const remainingMs =
      WS_RECONNECT_NOTIFICATION_GRACE_MS - Math.max(0, Date.now() - disconnectedAtMs);
    if (remainingMs <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setNowMs(Date.now());
    }, remainingMs + 50);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [documentVisible, status]);

  useEffect(() => {
    const uiState = getWsConnectionUiState(status);
    const previousUiState = previousUiStateRef.current;
    const previousDisconnectedAt = previousDisconnectedAtRef.current;
    const shouldShowOutageToast = shouldShowConnectionOutageToast({
      documentVisible,
      nowMs,
      status,
      uiState,
    });
    const shouldShowRecoveredToast = shouldShowConnectionRecoveredToast({
      outageToastVisible: outageToastVisibleRef.current,
      previousDisconnectedAt,
      previousUiState,
      uiState,
    });

    if (toastResetTimerRef.current !== null && shouldShowOutageToast) {
      window.clearTimeout(toastResetTimerRef.current);
      toastResetTimerRef.current = null;
    }

    if (shouldShowOutageToast) {
      const toastPayload = stackedThreadToast({
        actionProps: {
          children: t("common/retryNow_doycxx"),
          onClick: triggerManualReconnect,
        },
        data: {
          hideCopyButton: true,
          onClose: () => {
            outageToastVisibleRef.current = false;
            toastIdRef.current = null;
            toastKindRef.current = null;
          },
        },
        description:
          status.nextRetryAt === null
            ? t("common/reconnecting_rlqzwn", { "0": formatReconnectAttemptLabel(status) })
            : t("common/reconnectingIn_tnf3kx", {
                "0": formatRetryCountdown(status.nextRetryAt, nowMs),
                "1": formatReconnectAttemptLabel(status),
              }),
        timeout: 0,
        title: buildReconnectTitle(status),
        type: "loading",
      });

      if (toastIdRef.current) {
        toastManager.update(toastIdRef.current, toastPayload);
      } else {
        toastIdRef.current = toastManager.add(toastPayload);
      }
      toastKindRef.current = "outage";
      outageToastVisibleRef.current = true;
    } else if (toastIdRef.current && toastKindRef.current === "outage") {
      toastManager.close(toastIdRef.current);
      toastIdRef.current = null;
      toastKindRef.current = null;
      outageToastVisibleRef.current = false;
    }

    if (shouldShowRecoveredToast) {
      const successToast = {
        description: describeRecoveredToast(previousDisconnectedAt, status.connectedAt),
        title: buildRecoveredTitle(status),
        type: "success" as const,
        timeout: 0,
        data: {
          dismissAfterVisibleMs: 8_000,
          hideCopyButton: true,
        },
      };

      if (toastIdRef.current) {
        toastManager.update(toastIdRef.current, successToast);
      } else {
        toastIdRef.current = toastManager.add(successToast);
      }
      toastKindRef.current = "recovered";
      outageToastVisibleRef.current = false;

      toastResetTimerRef.current = window.setTimeout(() => {
        toastIdRef.current = null;
        toastKindRef.current = null;
        toastResetTimerRef.current = null;
      }, 8_250);
    }

    previousUiStateRef.current = uiState;
    previousDisconnectedAtRef.current = status.disconnectedAt;
  }, [documentVisible, nowMs, status]);

  useEffect(() => {
    return () => {
      if (toastResetTimerRef.current !== null) {
        window.clearTimeout(toastResetTimerRef.current);
      }
    };
  }, []);

  return null;
}

export function SlowRpcAckToastCoordinator() {
  const slowRequests = useSlowRpcAckRequests();
  const status = useWsConnectionStatus();
  const toastIdRef = useRef<ReturnType<typeof toastManager.add> | null>(null);

  useEffect(() => {
    if (getWsConnectionUiState(status) !== "connected") {
      if (toastIdRef.current) {
        toastManager.close(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    if (slowRequests.length === 0) {
      if (toastIdRef.current) {
        toastManager.close(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    const nextToast = {
      data: {
        expandableContent: <SlowRpcAckRequestDetails requests={slowRequests} />,
        expandableDescriptionTrigger: true,
        expandableLabels: {
          collapse: t("common/hideRequests_amzaf0"),
          expand: t("common/showRequests_4qlks9"),
        },
      },
      description: describeSlowRpcAckToast(slowRequests),
      timeout: 0,
      title: t("common/someRequestsAreSlow_2j1zs4"),
      type: "warning" as const,
    };

    if (toastIdRef.current) {
      toastManager.update(toastIdRef.current, nextToast);
    } else {
      toastIdRef.current = toastManager.add(nextToast);
    }
  }, [slowRequests, status]);

  return null;
}

export function WebSocketConnectionSurface({ children }: { readonly children: ReactNode }) {
  return children;
}
